import React, { useState } from 'react'
import { Button, Input, Text, Label } from '@flatfile/baseline'
import { signIn, signUp, authClient } from './betterAuthClient'

interface EmailAuthFormProps {
  onSuccess?: () => void
  onError?: (error: string) => void
}

export const EmailAuthForm: React.FC<EmailAuthFormProps> = ({
  onSuccess,
  onError,
}) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [isSignUp, setIsSignUp] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    try {
      const result = await authClient.signIn.social({
        provider: 'google',
        callbackURL: window.location.origin,
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      onSuccess?.()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Google sign-in failed'
      onError?.(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (isSignUp) {
        const result = await signUp.email({
          email,
          password,
          name,
        })

        if (result.error) {
          throw new Error(result.error.message)
        }
      } else {
        const result = await signIn.email({
          email,
          password,
        })

        if (result.error) {
          throw new Error(result.error.message)
        }
      }

      onSuccess?.()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Authentication failed'
      onError?.(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='p-6 bg-base-white rounded-lg shadow-md'>
      <Text variant='title1' as='h2' className='mb-6 text-center'>
        {isSignUp ? 'Sign Up' : 'Sign In'}
      </Text>

      {/* Google Sign-In Button */}
      <Button
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        variant='secondary'
        className='w-full mb-4'
        size='lg'
      >
        <svg className='w-5 h-5 mr-2' viewBox='0 0 24 24'>
          <path
            fill='currentColor'
            d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
          />
          <path
            fill='currentColor'
            d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
          />
          <path
            fill='currentColor'
            d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
          />
          <path
            fill='currentColor'
            d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
          />
        </svg>
        Continue with Google
      </Button>

      <div className='flex items-center mb-4'>
        <div className='flex-1 border-t border-gray-300'></div>
        <div className='px-3 text-sm text-gray-500'>or</div>
        <div className='flex-1 border-t border-gray-300'></div>
      </div>

      <form onSubmit={handleSubmit} className='space-y-4'>
        {isSignUp && (
          <div className='space-y-2'>
            <Label htmlFor='name' className='font-medium'>
              Name
            </Label>
            <Input
              type='text'
              id='name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required={isSignUp}
              placeholder='Enter your name'
              size='large'
              className='w-full'
            />
          </div>
        )}

        <div className='space-y-2'>
          <Label htmlFor='email' className='font-medium'>
            Email
          </Label>
          <Input
            type='email'
            id='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder='Enter your email'
            size='large'
            className='w-full'
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='password' className='font-medium'>
            Password
          </Label>
          <Input
            type='password'
            id='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder='Enter your password'
            minLength={6}
            size='large'
            className='w-full'
          />
        </div>

        <Button
          type='submit'
          disabled={isLoading}
          loading={isLoading}
          className='w-full'
          size='lg'
        >
          {isSignUp ? 'Sign Up' : 'Sign In'}
        </Button>
      </form>

      <div className='mt-6 text-center'>
        <Button
          variant='ghost'
          onClick={() => setIsSignUp(!isSignUp)}
          className='text-sm'
        >
          {isSignUp
            ? 'Already have an account? Sign in'
            : "Don't have an account? Sign up"}
        </Button>
      </div>
    </div>
  )
}
